import type { CamelToSnake, SubscriptionInjectableParams } from '@orus.eu/backend'
import { type productSelectionDimensions, productSelectionDimensionsByProduct } from '@orus.eu/dimensions-aggregate'
import { allProducts, getRetiredProductSuccessor } from '@orus.eu/product'
import { objectEntries } from '@orus.eu/utils'

import type { SearchParams } from '../routes/__root.tsx'

type InjectableUrlParamDefinition = {
  /** Absent when the frontend handles the parameter alone */
  backendKey?: keyof SubscriptionInjectableParams
  /** The product is not open yet: the funnel ignores the parameter until the flag is removed */
  unreleased?: true
  /** Read by the funnel but documented on no page */
  undocumented?: true
}

export type ProductSelectionDimensionName = (typeof productSelectionDimensions)[number]['name']

type ProductSelectionUrlParams = {
  [Name in ProductSelectionDimensionName as CamelToSnake<Name>]: {
    backendKey: Name
    unreleased?: true
  }
}

const PRODUCT_SELECTION_URL_PARAMS = {
  rcph_selected: { backendKey: 'rcphSelected' },
  rcpaxa_selected: { backendKey: 'rcpaxaSelected' },
  rcpa_selected: { backendKey: 'rcpaSelected' },
  mrph_selected: { backendKey: 'mrphSelected' },
  mrpw_selected: { backendKey: 'mrpwSelected' },
  mrpa_selected: { backendKey: 'mrpaSelected' },
  fr_prel_selected: { backendKey: 'frPrelSelected' },
  fr_rcpw_selected: { backendKey: 'frRcpwSelected' },
  fr_rccw_selected: { backendKey: 'frRccwSelected' },
  fr_aupa_selected: { backendKey: 'frAupaSelected', unreleased: true },
  fr_mrga_selected: { backendKey: 'frMrgaSelected', unreleased: true },
  fr_mrpaxe_selected: { backendKey: 'frMrpaxeSelected' },
  fr_rcpaxe_selected: { backendKey: 'frRcpaxeSelected' },
  nl_rcph_selected: { backendKey: 'nlRcphSelected' },
  nl_aovql_selected: { backendKey: 'nlAovqlSelected', unreleased: true },
  muta_selected: { backendKey: 'mutaSelected' },
  rcda_selected: { backendKey: 'rcdaSelected' },
  es_rcph_selected: { backendKey: 'esRcphSelected' },
  es_mrpw_selected: { backendKey: 'esMrpwSelected' },
} as const satisfies ProductSelectionUrlParams

export const INJECTABLE_URL_PARAMS = {
  embedding_partner: { backendKey: 'embeddingPartner' },
  organization: {},
  embed_in_iframe: {},
  partner_id: { backendKey: 'partner_id' },
  sso: { backendKey: 'firstSsoIntent' },
  category: { backendKey: 'category' },
  activity: { backendKey: 'activity' },
  activity_name: { backendKey: 'activity_name' },
  has_existing_health_insurance: {},
  first_name: { backendKey: 'firstName' },
  last_name: { backendKey: 'lastName' },
  email: { backendKey: 'email' },
  phone: { backendKey: 'phone' },
  siret: { backendKey: 'siretData' },
  siren: { backendKey: 'companyIdNumber' },
  cif: { backendKey: 'companyIdNumber' },
  nif: { backendKey: 'companyIdNumber' },
  ape: {},
  revenue: { backendKey: 'estimatedRevenue' },
  social_security_regime: { backendKey: 'socialSecurityRegime' },
  discount: { backendKey: 'discountCode' },
  discount_code: { backendKey: 'discountCode', undocumented: true },
  referral_code: { backendKey: 'referralCode' },
  operating_zone: { backendKey: 'operatingZone' },
  language: {},
  rcpa_persona: { backendKey: 'rcpaPersona' },
  mrp_selected: { backendKey: 'mrpSelected' },
  utm_source: { undocumented: true },
  utm_campaign: { undocumented: true },
  ...PRODUCT_SELECTION_URL_PARAMS,
} as const satisfies Partial<Record<keyof SearchParams, InjectableUrlParamDefinition>>

export type InjectableUrlParam = keyof typeof INJECTABLE_URL_PARAMS

export type DocumentedInjectableUrlParam = {
  [Param in InjectableUrlParam]: (typeof INJECTABLE_URL_PARAMS)[Param] extends { undocumented: true } ? never : Param
}[InjectableUrlParam]

type ProductSelectionUrlParam = keyof typeof PRODUCT_SELECTION_URL_PARAMS

const productSelectionUrlParamByDimensionName: Partial<
  Record<ProductSelectionDimensionName, ProductSelectionUrlParam>
> = Object.fromEntries(
  objectEntries(PRODUCT_SELECTION_URL_PARAMS).map(([urlParam, definition]) => [definition.backendKey, urlParam]),
)

/**
 * The parameter of a retired product, which links built before its retirement still carry, mapped to the parameter of
 * its successor (see `getRetiredProductSuccessor`): the funnel reads it as the successor's.
 */
const SUCCESSOR_URL_PARAM_BY_RETIRED_PRODUCT_URL_PARAM: Partial<
  Record<ProductSelectionUrlParam, ProductSelectionUrlParam>
> = Object.fromEntries(
  allProducts.flatMap((product) => {
    const successor = getRetiredProductSuccessor(product)
    if (!successor) return []
    const urlParam = productSelectionUrlParamByDimensionName[productSelectionDimensionsByProduct[product].name]
    const successorUrlParam =
      productSelectionUrlParamByDimensionName[productSelectionDimensionsByProduct[successor].name]
    return urlParam && successorUrlParam ? [[urlParam, successorUrlParam]] : []
  }),
)

/** The parameter that replaces a retired product's parameter, or undefined when the parameter is not replaced. */
export function getReplacingUrlParam(urlParam: InjectableUrlParam): InjectableUrlParam | undefined {
  return isProductSelectionUrlParam(urlParam) ? SUCCESSOR_URL_PARAM_BY_RETIRED_PRODUCT_URL_PARAM[urlParam] : undefined
}

function isProductSelectionUrlParam(urlParam: string): urlParam is ProductSelectionUrlParam {
  return urlParam in PRODUCT_SELECTION_URL_PARAMS
}

export const PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME: Partial<
  Record<ProductSelectionDimensionName, InjectableUrlParam>
> = {}
/** The parameter of the retired product, read when the replacement's own parameter is absent */
export const LEGACY_PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME: Partial<
  Record<ProductSelectionDimensionName, InjectableUrlParam>
> = {}
for (const [urlParam, definition] of objectEntries(PRODUCT_SELECTION_URL_PARAMS)) {
  if ('unreleased' in definition) continue
  const successorUrlParam = SUCCESSOR_URL_PARAM_BY_RETIRED_PRODUCT_URL_PARAM[urlParam]
  if (successorUrlParam) {
    LEGACY_PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME[PRODUCT_SELECTION_URL_PARAMS[successorUrlParam].backendKey] =
      urlParam
  } else {
    PRODUCT_SELECTION_URL_PARAM_BY_DIMENSION_NAME[definition.backendKey] = urlParam
  }
}
